import { useState, useMemo } from "react";
import api, { useList, errMsg, fmtRM } from "@/lib/api";
import { PageHeader, Spinner, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Settings, TrendingUp, GripVertical, Pencil, FolderPlus, ArrowRight,
         Download, Upload } from "lucide-react";
import { Link } from "react-router-dom";

const PROTECTED_CATEGORIES = ["Piano", "Guitar", "Drum", "Vocal", "Violin", "Saxophone",
                              "Electone", "Junior Music", "Music Theory", "Other"];

export default function CoursesBoardPage() {
  const { data: courses, loading, reload: reloadCourses } = useList("/courses");
  const { data: fees, reload: reloadFees } = useList("/course-fees");
  const { data: settings } = useList("/fee-settings/active");
  const [draggingId, setDraggingId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [editCourse, setEditCourse] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [extraCategories, setExtraCategories] = useState([]);

  if (loading || !courses) return <Spinner full />;

  // Build category -> [course] map, with fee lookup
  const feeByCourse = {};
  (fees || []).forEach((f) => {
    if (f.status === "active") {
      feeByCourse[f.course_id] = f.music_course_fee;
    }
  });

  const categoryMap = new Map();
  // Seed with protected + extra categories so empty columns are visible
  [...PROTECTED_CATEGORIES, ...extraCategories].forEach((c) => categoryMap.set(c, []));
  courses.forEach((c) => {
    const cat = (c.category || "Other").trim() || "Other";
    if (!categoryMap.has(cat)) categoryMap.set(cat, []);
    categoryMap.get(cat).push(c);
  });
  const sortedCategories = [...categoryMap.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([cat, list]) => [cat, list.sort((a, b) => a.name.localeCompare(b.name))]);

  const onDragStart = (e, id) => {
    setDraggingId(id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", id);
  };
  const onDragOver = (e, cat) => { e.preventDefault(); setDropTarget(cat); };
  const onDragLeave = () => setDropTarget(null);
  const onDrop = async (e, newCat) => {
    e.preventDefault();
    setDropTarget(null);
    const id = e.dataTransfer.getData("text/plain") || draggingId;
    setDraggingId(null);
    if (!id) return;
    const course = courses.find((c) => c.id === id);
    if (!course || course.category === newCat) return;
    try {
      await api.post("/courses/bulk-category",
                      { course_ids: [id], new_category: newCat });
      toast.success(`Moved "${course.name}" → ${newCat}`);
      reloadCourses();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const createCategory = () => {
    const name = newCatName.trim();
    if (!name) return;
    if (categoryMap.has(name)) { toast.error("Category already exists"); return; }
    setExtraCategories([...extraCategories, name]);
    setNewCatName("");
    toast.success(`Category "${name}" added — drag a course here to populate it`);
  };

  const adminFee = settings?.default_school_admin_fee;
  const sstRate = settings?.default_sst_rate;

  return (
    <div data-testid="courses-board-page">
      <PageHeader title="Music Course Board"
        description="Drag a course card to move it between categories. Click any card to edit name + fees. Use Bulk Adjust to raise/lower fees across a whole category." />

      {/* Toolbar */}
      <div className="rounded-lg border bg-card p-4 mb-5 flex flex-wrap gap-3 items-end" data-testid="board-toolbar">
        <div className="flex items-end gap-2">
          <div className="space-y-1">
            <Label className="text-xs">New category name</Label>
            <Input value={newCatName} onChange={(e) => setNewCatName(e.target.value)}
                    placeholder="e.g. Strings ensemble" className="w-56"
                    data-testid="new-category-input" />
          </div>
          <Button size="sm" variant="outline" onClick={createCategory} data-testid="add-category-btn">
            <FolderPlus className="h-4 w-4 mr-1" />Add category
          </Button>
        </div>
        <div className="flex-1 min-w-[20px]"></div>
        <Button size="sm" variant="outline" onClick={() => setBulkOpen(true)} data-testid="bulk-fee-btn">
          <TrendingUp className="h-4 w-4 mr-1" />Bulk adjust fees
        </Button>
        <Button size="sm" variant="outline" onClick={() => setCsvOpen(true)} data-testid="csv-tools-btn">
          <Upload className="h-4 w-4 mr-1" />CSV import / export
        </Button>
        <Button asChild size="sm" variant="outline">
          <Link to="/admin/fee-settings" data-testid="admin-fee-link">
            <Settings className="h-4 w-4 mr-1" />School admin fee &amp; SST{" "}
            {adminFee != null && <span className="ml-1 text-xs text-muted-foreground">({fmtRM(adminFee)} · {(sstRate * 100).toFixed(0)}% SST)</span>}
          </Link>
        </Button>
      </div>

      {/* Board */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="board-grid">
        {sortedCategories.map(([cat, list]) => (
          <div key={cat}
               onDragOver={(e) => onDragOver(e, cat)}
               onDragLeave={onDragLeave}
               onDrop={(e) => onDrop(e, cat)}
               className={`rounded-lg border bg-card p-4 min-h-[200px] transition ${dropTarget === cat ? "border-secondary border-2 bg-secondary/5" : ""}`}
               data-testid={`board-column-${cat}`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-secondary">
                {cat}
              </h3>
              <span className="text-xs text-muted-foreground bg-muted rounded-full px-2 py-0.5">{list.length}</span>
            </div>
            <div className="space-y-2">
              {list.length === 0 ? (
                <p className="text-xs text-muted-foreground italic py-4 text-center">Drop courses here</p>
              ) : list.map((c) => (
                <div key={c.id} draggable
                     onDragStart={(e) => onDragStart(e, c.id)}
                     onClick={() => setEditCourse({ ...c, fee: feeByCourse[c.id] })}
                     className={`group rounded-md border bg-background p-2.5 cursor-grab active:cursor-grabbing transition ${draggingId === c.id ? "opacity-50" : "hover:border-secondary/40 hover:shadow-sm"}`}
                     data-testid={`board-card-${c.id}`}>
                  <div className="flex items-start gap-2">
                    <GripVertical className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{c.name}</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-[10px] text-muted-foreground">
                          {feeByCourse[c.id] != null ? fmtRM(feeByCourse[c.id]) + "/mo" : "no fee set"}
                        </span>
                        <Pencil className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <CourseEditDialog open={!!editCourse} course={editCourse} onClose={() => setEditCourse(null)}
                        onSaved={() => { setEditCourse(null); reloadCourses(); reloadFees(); }} />

      <BulkFeeAdjustDialog open={bulkOpen} onClose={() => setBulkOpen(false)}
                            categories={[...categoryMap.keys()].sort()}
                            masters={[...new Set(courses.map((c) => c.master_course_name).filter(Boolean))].sort()}
                            onSaved={() => { setBulkOpen(false); reloadFees(); }} />

      <CsvToolsDialog open={csvOpen} onClose={() => setCsvOpen(false)}
                       onApplied={() => { setCsvOpen(false); reloadCourses(); reloadFees(); }} />
    </div>
  );
}


// --------------------- Course edit dialog ---------------------
export function CourseEditDialog({ open, course, onClose, onSaved }) {
  const [form, setForm] = useState(course || {});
  const [fee, setFee] = useState("");
  const [regFee, setRegFee] = useState("");
  const [materialFee, setMaterialFee] = useState("");
  const [trialFee, setTrialFee] = useState("");
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);

  // Load the active fee whenever we open a course
  useMemo(() => {
    setForm(course || {});
    if (course) {
      setFee(course.fee != null ? String(course.fee) : "");
      // Will be populated below via the active fee fetch
      setRegFee(""); setMaterialFee(""); setTrialFee("");
      (async () => {
        try {
          const r = await api.get(`/course-fees?course_id=${course.id}&status=active`);
          const active = (r.data || []).find((f) => f.status === "active");
          if (active) {
            setRegFee(String(active.registration_fee ?? "72"));
            setMaterialFee(String(active.material_fee ?? "30"));
            setTrialFee(String(active.trial_fee ?? "30"));
          } else {
            setRegFee("50"); setMaterialFee("30"); setTrialFee("30");
          }
        } catch { /* fall through */ }
      })();
    }
  }, [course]);

  if (!open || !course) return null;
  const isNew = !course.id;
  const save = async () => {
    if (!form.name?.trim()) { toast.error("Course name is required"); return; }
    setBusy(true);
    try {
      let savedCourse = course;
      if (isNew) {
        // Create new course
        const r = await api.post("/courses", {
          name: form.name, category: form.category || "Other",
          description: form.description || "",
          status: form.status || "active",
          master_course_name: form.master_course_name || null,
          level_within_master: form.level_within_master || null,
          class_type: form.class_type || "individual",
          duration_minutes: Number(form.duration_minutes) || (form.class_type === "group" ? 60 : 30),
          public_visible: !!form.public_visible,
        });
        savedCourse = r.data;
      } else {
        // Update existing course doc
        await api.put(`/courses/${course.id}`, {
          name: form.name, category: form.category,
          description: form.description, status: form.status,
          master_course_name: form.master_course_name,
          level_within_master: form.level_within_master,
          class_type: form.class_type || "individual",
          duration_minutes: Number(form.duration_minutes) || 60,
          public_visible: !!form.public_visible,
        });
      }
      // If ANY fee changed (or this is a new course with a fee), write a new effective-dated course_fee row
      const newFee = parseFloat(fee);
      const newReg = parseFloat(regFee);
      const newMat = parseFloat(materialFee);
      const newTrial = parseFloat(trialFee);
      const anyFeeChange = isNew
        ? (!isNaN(newFee) && newFee > 0)
        : ((!isNaN(newFee) && newFee > 0 && newFee !== course.fee)
            || !isNaN(newReg) || !isNaN(newMat) || !isNaN(newTrial));
      if (anyFeeChange) {
        await api.post("/course-fees", {
          course_id: savedCourse.id, level_id: null,
          music_course_fee: isNaN(newFee) ? (course.fee || 0) : newFee,
          registration_fee: isNaN(newReg) ? 72 : newReg,
          material_fee: isNaN(newMat) ? 30 : newMat,
          trial_fee: isNaN(newTrial) ? 30 : newTrial,
          effective_start_date: effectiveDate,
          status: "active",
        });
      }
      toast.success(isNew ? `Course "${form.name}" created` : "Course saved");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="course-edit-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "Add a new course" : "Edit course"}</DialogTitle>
          <DialogDescription>{isNew ? "Pick a category and (optionally) a master series. Add fees so it's bookable straight away." : "Change name, category, or any fee. Fee changes are effective-dated — past invoices stay untouched."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 pr-1">
          <div className="space-y-1.5">
            <Label className="text-xs">Course name</Label>
            <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-course-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Category</Label>
              <Input value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="edit-course-category" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Master series (optional)</Label>
              <Input value={form.master_course_name || ""} onChange={(e) => setForm({ ...form, master_course_name: e.target.value })}
                     placeholder='e.g. "Yamaha Piano Course"' data-testid="edit-course-master" />
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Level (within master)</Label>
              <Input type="number" min="1" value={form.level_within_master || ""}
                     onChange={(e) => setForm({ ...form, level_within_master: e.target.value ? Number(e.target.value) : null })}
                     placeholder="e.g. 1" data-testid="edit-course-level" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Class type</Label>
              <select value={form.class_type || "individual"}
                      onChange={(e) => {
                        const ct = e.target.value;
                        // Auto-snap duration to the new class-type default,
                        // but only if the user hasn't customised it away from the
                        // OPPOSITE type's default — keeps explicit choices sticky.
                        const otherDefault = ct === "individual" ? 60 : 30;
                        const next = { ...form, class_type: ct };
                        if (Number(form.duration_minutes) === otherDefault || !form.duration_minutes) {
                          next.duration_minutes = ct === "individual" ? 30 : 60;
                        }
                        setForm(next);
                      }}
                      data-testid="edit-course-class-type"
                      className="w-full rounded-md border px-3 py-2 bg-background text-sm">
                <option value="individual">Individual (default 30 min)</option>
                <option value="group">Group (default 60 min)</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Lesson duration</Label>
              <select value={form.duration_minutes ?? ((form.class_type === "group") ? 60 : 30)}
                      onChange={(e) => setForm({ ...form, duration_minutes: Number(e.target.value) })}
                      data-testid="edit-course-duration"
                      className="w-full rounded-md border px-3 py-2 bg-background text-sm">
                <option value={30}>30 minutes</option>
                <option value={45}>45 minutes</option>
                <option value={60}>60 minutes</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Status</Label>
              <select value={form.status || "active"}
                      onChange={(e) => setForm({ ...form, status: e.target.value })}
                      data-testid="edit-course-status"
                      className="w-full rounded-md border px-3 py-2 bg-background text-sm">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="rounded-md border bg-muted/30 p-3 space-y-3">
            <p className="text-xs font-bold uppercase tracking-wider text-secondary">Fees (effective {effectiveDate})</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Monthly course fee (RM)</Label>
                <Input type="number" step="0.01" value={fee} onChange={(e) => setFee(e.target.value)} data-testid="edit-course-fee" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Registration fee (RM)</Label>
                <Input type="number" step="0.01" value={regFee} onChange={(e) => setRegFee(e.target.value)} data-testid="edit-course-reg-fee" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Material fee (RM)</Label>
                <Input type="number" step="0.01" value={materialFee} onChange={(e) => setMaterialFee(e.target.value)} data-testid="edit-course-material-fee" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Trial fee (RM)</Label>
                <Input type="number" step="0.01" value={trialFee} onChange={(e) => setTrialFee(e.target.value)} data-testid="edit-course-trial-fee" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Effective from</Label>
              <Input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} data-testid="edit-course-effective" />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={!!form.public_visible}
                    onChange={(e) => setForm({ ...form, public_visible: e.target.checked })}
                    data-testid="edit-course-public" />
            Show on public website
          </label>
          <div className="space-y-1.5">
            <Label className="text-xs">Description</Label>
            <textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })}
                       className="w-full rounded-md border px-3 py-2 bg-background text-sm" data-testid="edit-course-description" />
          </div>

          {/* Course image */}
          {course.id && (
            <div className="rounded-md border bg-muted/30 p-3 space-y-3" data-testid="image-block">
              <p className="text-xs font-bold uppercase tracking-wider text-secondary">Course image</p>
              <div className="flex items-start gap-3">
                {form.image_url ? (
                  <img src={form.image_url.startsWith("http") ? form.image_url
                          : `${process.env.REACT_APP_BACKEND_URL}${form.image_url}`}
                       alt={form.image_alt_text || form.name}
                       className="h-20 w-32 rounded object-cover border" />
                ) : (
                  <div className="h-20 w-32 rounded border bg-card flex items-center justify-center text-[10px] text-muted-foreground">
                    No image
                  </div>
                )}
                <div className="flex-1 space-y-2">
                  <Input value={form.image_url || ""} placeholder="Image URL or /api/uploads/..."
                         onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                         data-testid="image-url-input" />
                  <Input value={form.image_alt_text || ""} placeholder="Alt text (for accessibility)"
                         onChange={(e) => setForm({ ...form, image_alt_text: e.target.value })}
                         data-testid="image-alt-input" />
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" data-testid="image-import-url-btn"
                        onClick={async () => {
                          const url = window.prompt("Paste the image URL to import:");
                          if (!url) return;
                          try {
                            const r = await api.post(`/courses/${course.id}/image/import`,
                              { image_url: url, alt_text: form.image_alt_text || form.name });
                            setForm({ ...form, image_url: r.data.image_url,
                                       image_alt_text: r.data.image_alt_text,
                                       image_source_url: r.data.image_source_url });
                            toast.success("Image imported");
                          } catch (e) { toast.error(errMsg(e)); }
                        }}>
                  Import from URL
                </Button>
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" className="hidden"
                         data-testid="image-upload-input"
                         onChange={async (e) => {
                           const f = e.target.files?.[0];
                           if (!f) return;
                           const fd = new FormData();
                           fd.append("file", f);
                           try {
                             const r = await api.post(`/courses/${course.id}/image/upload`, fd,
                               { headers: { "Content-Type": "multipart/form-data" } });
                             setForm({ ...form, image_url: r.data.image_url,
                                        image_source_url: null });
                             toast.success("Image uploaded");
                           } catch (err) { toast.error(errMsg(err)); }
                           e.target.value = "";
                         }} />
                  <span className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-xs hover:bg-muted/40">
                    Upload from computer
                  </span>
                </label>
                {form.image_url && (
                  <Button size="sm" variant="outline" data-testid="image-remove-btn"
                          className="text-rose-700 border-rose-200"
                          onClick={async () => {
                            if (!window.confirm("Remove the image from this course?")) return;
                            try {
                              await api.delete(`/courses/${course.id}/image`);
                              setForm({ ...form, image_url: null, image_alt_text: null });
                              toast.success("Image removed");
                            } catch (err) { toast.error(errMsg(err)); }
                          }}>
                    Remove
                  </Button>
                )}
              </div>
              {form.image_source_url && (
                <p className="text-[10px] text-muted-foreground" data-testid="image-source">
                  Source: <a href={form.image_source_url} target="_blank" rel="noreferrer" className="underline">{form.image_source_url}</a>
                </p>
              )}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="save-course-btn">{busy ? "Saving…" : "Save changes"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// --------------------- Bulk fee adjustment dialog ---------------------
export function BulkFeeAdjustDialog({ open, onClose, categories, masters = [], selectedIds = [], onSaved }) {
  const [scope, setScope] = useState(selectedIds.length > 0 ? "selected" : "category");
  const [category, setCategory] = useState("");
  const [master, setMaster] = useState("");
  const [mode, setMode] = useState("percent");
  const [direction, setDirection] = useState("increase");
  const [value, setValue] = useState("5");
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const apply = async () => {
    const numericValue = parseFloat(value);
    if (isNaN(numericValue) || numericValue < 0) {
      toast.error("Enter a positive number — direction handles increase/decrease");
      return;
    }
    if (scope === "category" && !category) { toast.error("Pick a category"); return; }
    if (scope === "master" && !master) { toast.error("Pick a master series"); return; }
    if (scope === "selected" && selectedIds.length === 0) { toast.error("No courses selected"); return; }

    const sign = (mode !== "set" && direction === "decrease") ? "-" : (mode !== "set" ? "+" : "");
    const valLabel = mode === "set"
      ? `Set to RM ${numericValue}`
      : mode === "percent"
      ? `${sign}${numericValue}%`
      : `${sign}RM ${numericValue}`;
    const scopeLabel = scope === "all" ? "ALL courses"
                     : scope === "category" ? `category "${category}"`
                     : scope === "master" ? `master "${master}"`
                     : `${selectedIds.length} selected course(s)`;
    if (!window.confirm(`Apply: ${valLabel} to ${scopeLabel}, effective ${effectiveDate}?`)) return;

    setBusy(true);
    try {
      const body = {
        mode, direction, value: numericValue, effective_start_date: effectiveDate,
      };
      if (scope === "all") body.apply_to_all = true;
      else if (scope === "category") body.category = category;
      else if (scope === "master") body.master_course_name = master;
      else body.course_ids = selectedIds;

      const r = await api.post("/course-fees/bulk-adjust", body);
      toast.success(`Updated ${r.data.fees_changed} fee(s) across ${r.data.courses_targeted} course(s)`);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="bulk-fee-dialog">
        <DialogHeader>
          <DialogTitle>Bulk adjust music course fees</DialogTitle>
          <DialogDescription>Increase or decrease fees across a category, master series, all courses, or just the ones you ticked. Old fees are kept as history.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Apply to</Label>
            <select value={scope} onChange={(e) => setScope(e.target.value)}
                    className="w-full rounded-md border px-3 py-2 bg-background text-sm"
                    data-testid="bulk-scope-select">
              {selectedIds.length > 0 && (
                <option value="selected">Selected courses ({selectedIds.length})</option>
              )}
              <option value="category">A whole category</option>
              <option value="master">A whole master series</option>
              <option value="all">ALL courses (use with care)</option>
            </select>
          </div>
          {scope === "category" && (
            <div className="space-y-1.5">
              <Label className="text-xs">Category</Label>
              <select value={category} onChange={(e) => setCategory(e.target.value)}
                      className="w-full rounded-md border px-3 py-2 bg-background text-sm"
                      data-testid="bulk-category-select">
                <option value="">— Pick a category —</option>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          )}
          {scope === "master" && (
            <div className="space-y-1.5">
              <Label className="text-xs">Master series</Label>
              <select value={master} onChange={(e) => setMaster(e.target.value)}
                      className="w-full rounded-md border px-3 py-2 bg-background text-sm"
                      data-testid="bulk-master-select">
                <option value="">— Pick a master —</option>
                {masters.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Mode</Label>
              <select value={mode} onChange={(e) => setMode(e.target.value)}
                      className="w-full rounded-md border px-3 py-2 bg-background text-sm"
                      data-testid="bulk-mode-select">
                <option value="percent">Adjust by %</option>
                <option value="absolute">Adjust by RM</option>
                <option value="set">Set to fixed RM</option>
              </select>
            </div>
            {mode !== "set" ? (
              <div className="space-y-1.5">
                <Label className="text-xs">Direction</Label>
                <div className="inline-flex w-full rounded-md border bg-muted/30 p-1 text-xs">
                  <button type="button" onClick={() => setDirection("increase")}
                          data-testid="bulk-dir-increase"
                          className={`flex-1 px-3 py-1.5 rounded transition-colors ${direction === "increase"
                            ? "bg-emerald-100 text-emerald-800 border border-emerald-200" : "text-muted-foreground"}`}>
                    ▲ Increase
                  </button>
                  <button type="button" onClick={() => setDirection("decrease")}
                          data-testid="bulk-dir-decrease"
                          className={`flex-1 px-3 py-1.5 rounded transition-colors ${direction === "decrease"
                            ? "bg-rose-100 text-rose-800 border border-rose-200" : "text-muted-foreground"}`}>
                    ▼ Decrease
                  </button>
                </div>
              </div>
            ) : <div /> }
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">
              {mode === "set" ? "New fee (RM)"
                : mode === "percent" ? "Percent (e.g. 5 means 5%)"
                : "Amount (RM)"}
            </Label>
            <Input type="number" step="0.01" min="0" value={value} onChange={(e) => setValue(e.target.value)}
                    data-testid="bulk-value-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Effective from</Label>
            <Input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)}
                    data-testid="bulk-effective-date" />
            <p className="text-[11px] text-muted-foreground">
              Past invoices remain unchanged; this date is when the new fee takes effect for future billing.
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={apply} disabled={busy} data-testid="apply-bulk-btn">
            {busy ? "Applying…" : "Apply"} <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// --------------------- CSV export / import dialog ---------------------
export function CsvToolsDialog({ open, onClose, onApplied }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const downloadCsv = () => {
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    const a = document.createElement("a");
    a.href = `${apiBase}/api/admin/courses/export-csv`;
    a.target = "_blank";
    a.click();
  };

  const doPreview = async () => {
    if (!file) { toast.error("Pick a CSV file first"); return; }
    setBusy(true); setPreview(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/admin/courses/import-csv?dry_run=true", form,
        { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const applyImport = async () => {
    if (!file) return;
    if (!window.confirm(
      `Apply changes? This will:\n` +
      `  • Create ${preview.created.length} new course(s)\n` +
      `  • Update ${preview.updated.length} course(s)\n` +
      `  • Soft-delete ${preview.deleted.length} course(s)\n` +
      `  • Change ${preview.fee_changed.length} fee(s)\n\n` +
      `Past invoices keep their original fees.`)) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/admin/courses/import-csv?dry_run=false", form,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(
        `Done: ${r.data.created.length} created · ${r.data.updated.length} updated · ` +
        `${r.data.deleted.length} deleted · ${r.data.fee_changed.length} fee(s) updated`);
      onApplied();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="csv-tools-dialog">
        <DialogHeader>
          <DialogTitle>CSV import &amp; export</DialogTitle>
          <DialogDescription>
            Export every course + active fee as CSV, edit it in Excel/Google Sheets, then upload the file to bulk-create, bulk-update or bulk-delete courses + fees. The system writes effective-dated fee rows so past invoices are untouched.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="rounded-md border bg-muted/30 p-4">
            <p className="text-sm font-semibold mb-1">1) Download current data</p>
            <p className="text-xs text-muted-foreground mb-3">
              Columns: id, name, category, master_course_name, level_within_master, monthly_fee, registration_fee, material_fee, trial_fee, public_visible, status, description.
              <br />Keep the <b>id</b> column when editing existing courses. Leave <b>id</b> blank to create a new course. Set <b>status=deleted</b> to soft-delete.
            </p>
            <Button size="sm" variant="outline" onClick={downloadCsv} data-testid="csv-download-btn">
              <Download className="h-4 w-4 mr-1" />Download music_courses.csv
            </Button>
          </div>

          <div className="rounded-md border bg-muted/30 p-4">
            <p className="text-sm font-semibold mb-3">2) Upload edited CSV</p>
            <div className="flex items-end gap-3">
              <input type="file" accept=".csv,text/csv"
                      onChange={(e) => { setFile(e.target.files?.[0]); setPreview(null); }}
                      className="flex-1 text-sm" data-testid="csv-upload-input" />
              <Button size="sm" variant="outline" onClick={doPreview} disabled={!file || busy} data-testid="csv-preview-btn">
                Preview changes
              </Button>
            </div>
            {preview && (
              <div className="mt-4 rounded-md border bg-background p-3 text-sm" data-testid="csv-preview-summary">
                <p className="font-semibold mb-2">Preview (dry-run — nothing applied yet):</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  <p>📋 Created: <b>{preview.created.length}</b></p>
                  <p>✏️ Updated: <b>{preview.updated.length}</b></p>
                  <p>🗑️ Deleted: <b>{preview.deleted.length}</b></p>
                  <p>💰 Fees changed: <b>{preview.fee_changed.length}</b></p>
                </div>
                {preview.errors.length > 0 && (
                  <div className="mt-2 text-destructive text-xs">
                    <p className="font-semibold">⚠️ {preview.errors.length} row(s) had errors:</p>
                    <ul className="ml-4 list-disc">
                      {preview.errors.slice(0, 5).map((e, i) =>
                        <li key={i}>Row {e.row}: {e.msg}</li>)}
                    </ul>
                  </div>
                )}
                <Button className="mt-3" onClick={applyImport} disabled={busy} data-testid="csv-apply-btn">
                  Apply changes
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

